import { GridAdapter } from "./adapters/GridAdapter";
import {
  DEFAULT_GRID_LAYOUT_PRESETS,
  GridInstance,
  GridLayout,
  GridLayoutDevice,
  GridLayoutMode,
  getGridLayoutMode,
  resolveGridLayoutDevice,
} from "./models/GridInstance";
import { StringDevUIBuilder as GridUIBuilder } from "../core/ui/StringDevUIBuilder";

type DropPlacement = "before" | "after";

function getDeviceLabel(device: GridLayoutDevice): string {
  if (device === "desktop") return "Desktop";
  if (device === "tablet") return "Tablet";
  return "Phone";
}

function getModeLabel(mode: GridLayoutMode): string {
  return mode;
}

function getLayoutSummaryLabel(layout: GridLayout): string {
  const trimmedName = layout.name.trim();
  const usesDefaultName =
    /^Layout \d+$/i.test(trimmedName) ||
    DEFAULT_GRID_LAYOUT_PRESETS.some((preset) => preset.name === trimmedName);
  const baseLabel =
    trimmedName.length > 0 && !usesDefaultName
      ? trimmedName
      : trimmedName.length > 0
        ? trimmedName
        : getDeviceLabel(resolveGridLayoutDevice(layout.minWidth));
  return `${baseLabel} - ${layout.minWidth}px`;
}

export class GridHUD {
  private target: HTMLElement;
  private adapterRegistry: Map<string, GridAdapter>;
  private chromeRoot: HTMLDivElement;
  private trigger: HTMLButtonElement;
  private panel: HTMLDivElement;
  private layoutTitle!: HTMLSpanElement;
  private layoutChips!: HTMLDivElement;
  private layoutSettingsToggle!: HTMLButtonElement;
  private layoutSettings!: HTMLDivElement;
  private layoutSettingsList!: HTMLDivElement;
  private layoutIOActions!: HTMLDivElement;
  private listContainer!: HTMLDivElement;
  private addGuideButton!: HTMLButtonElement;
  private addPanel!: HTMLDivElement;
  private addGridList!: HTMLDivElement;
  private layoutPanel!: HTMLDivElement;
  private detailPanel!: HTMLDivElement;
  private detailContent!: HTMLDivElement;
  private detailTitle: HTMLSpanElement | null = null;
  private detailIcon: HTMLSpanElement | null = null;
  private detailBuilder: GridUIBuilder | null = null;
  private instances: GridInstance[] = [];
  private layouts: GridLayout[] = [];
  private selectedLayoutId = "";
  private resolvedLayoutId = "";
  private selectedInstanceId: string | null = null;
  private isOpen = false;
  private isAddPanelOpen = false;
  private isLayoutSettingsOpen = false;
  private enabled = true;
  private onDocumentPointerDownBind!: (event: PointerEvent) => void;

  private onAdd!: (type: string) => void;
  private onRemove!: (id: string) => void;
  private onToggle!: (id: string) => void;
  private onSettingChange!: (id: string, key: string, value: any) => void;
  private onReorder!: (
    draggedId: string,
    targetId: string,
    placement: DropPlacement,
  ) => void;
  private onMoveToEnd!: (id: string) => void;
  private onSelectLayout!: (layoutId: string) => void;
  private onUpdateLayoutMinWidth!: (layoutId: string, minWidth: number) => void;
  private onPreviewResponsiveLayout!: (
    mediumMinWidth: number,
    largeMinWidth: number,
    activeLayoutId: string,
  ) => void;
  private onCommitResponsiveLayout!: (
    mediumMinWidth: number,
    largeMinWidth: number,
    activeLayoutId: string,
  ) => void;
  private onExport!: () => void;
  private onImport!: () => void;
  private onRenameInstance!: (instanceId: string, name: string) => void;
  private onHUDOpen!: () => void;
  private onHUDClose!: () => void;
  private onLayoutPanelOpen!: () => void;
  private onLayoutPanelClose!: () => void;

  constructor(
    target: HTMLElement,
    adapterRegistry: Map<string, GridAdapter>,
    callbacks: {
      onAdd: (type: string) => void;
      onRemove: (id: string) => void;
      onToggle: (id: string) => void;
      onSettingChange: (id: string, key: string, value: any) => void;
      onReorder: (
        draggedId: string,
        targetId: string,
        placement: DropPlacement,
      ) => void;
      onMoveToEnd: (id: string) => void;
      onSelectLayout: (layoutId: string) => void;
      onUpdateLayoutMinWidth: (layoutId: string, minWidth: number) => void;
      onPreviewResponsiveLayout: (
        mediumMinWidth: number,
        largeMinWidth: number,
        activeLayoutId: string,
      ) => void;
      onCommitResponsiveLayout: (
        mediumMinWidth: number,
        largeMinWidth: number,
        activeLayoutId: string,
      ) => void;
      onExport: () => void;
      onImport: () => void;
      onRenameInstance: (instanceId: string, name: string) => void;
      onHUDOpen: () => void;
      onHUDClose: () => void;
      onLayoutPanelOpen: () => void;
      onLayoutPanelClose: () => void;
    },
    options?: { attachTrigger?: boolean },
  ) {
    this.target = target;
    this.adapterRegistry = adapterRegistry;
    this.onAdd = callbacks.onAdd;
    this.onRemove = callbacks.onRemove;
    this.onToggle = callbacks.onToggle;
    this.onSettingChange = callbacks.onSettingChange;
    this.onReorder = callbacks.onReorder;
    this.onMoveToEnd = callbacks.onMoveToEnd;
    this.onSelectLayout = callbacks.onSelectLayout;
    this.onUpdateLayoutMinWidth = callbacks.onUpdateLayoutMinWidth;
    this.onPreviewResponsiveLayout = callbacks.onPreviewResponsiveLayout;
    this.onCommitResponsiveLayout = callbacks.onCommitResponsiveLayout;
    this.onExport = callbacks.onExport;
    this.onImport = callbacks.onImport;
    this.onRenameInstance = callbacks.onRenameInstance;
    this.onHUDOpen = callbacks.onHUDOpen;
    this.onHUDClose = callbacks.onHUDClose;
    this.onLayoutPanelOpen = callbacks.onLayoutPanelOpen;
    this.onLayoutPanelClose = callbacks.onLayoutPanelClose;
    this.onDocumentPointerDownBind = this.onDocumentPointerDown.bind(this);

    this.chromeRoot = this.createChromeRoot();
    this.trigger = this.createTrigger();
    this.panel = this.createMainPanel();
    this.addPanel = this.createAddPanel();
    this.layoutPanel = this.createLayoutPanel();
    this.detailPanel = this.createDetailPanel();

    if (options?.attachTrigger !== false) {
      this.chromeRoot.appendChild(this.trigger);
    }
    this.chromeRoot.appendChild(this.panel);
    this.chromeRoot.appendChild(this.addPanel);
    this.chromeRoot.appendChild(this.layoutPanel);
    this.chromeRoot.appendChild(this.detailPanel);
    document.addEventListener("pointerdown", this.onDocumentPointerDownBind);
  }

  refresh(
    instances: GridInstance[],
    layouts: GridLayout[],
    selectedLayoutId: string,
    resolvedLayoutId: string,
  ): void {
    this.instances = [...instances];
    this.layouts = [...layouts];
    this.selectedLayoutId = selectedLayoutId;
    this.resolvedLayoutId = resolvedLayoutId;
    if (
      this.selectedInstanceId &&
      !instances.some((instance) => instance.id === this.selectedInstanceId)
    ) {
      this.clearSelection();
    }
    const activeLayout =
      this.layouts.find((layout) => layout.id === resolvedLayoutId) ?? this.layouts[0];
    this.layoutTitle.textContent = activeLayout ? getLayoutSummaryLabel(activeLayout) : "Layout";
    this.renderLayoutChips();
    this.renderLayoutSettings();
    this.renderList();
    this.renderDetailPanel();
  }

  destroy(): void {
    this.detailBuilder?.destroy();
    document.removeEventListener("pointerdown", this.onDocumentPointerDownBind);
    this.chromeRoot.remove();
  }

  getTriggerElement(): HTMLButtonElement {
    return this.trigger;
  }

  getChromeElement(): HTMLDivElement {
    return this.chromeRoot;
  }

  setTargetId(targetId: string): void {
    this.chromeRoot.setAttribute("data-target-id", targetId);
    this.trigger.setAttribute("data-target-id", targetId);
    this.panel.setAttribute("data-target-id", targetId);
    this.addPanel.setAttribute("data-target-id", targetId);
    this.layoutPanel.setAttribute("data-target-id", targetId);
    this.detailPanel.setAttribute("data-target-id", targetId);
  }

  setAnchorPosition(docX: number, docY: number): void {
    this.chromeRoot.removeAttribute("hidden");
    this.chromeRoot.style.transform = `translate3d(${Math.round(docX * 100) / 100}px, ${Math.round(docY * 100) / 100}px, 0)`;
  }

  setAnchorVisible(visible: boolean): void {
    if (visible) {
      this.chromeRoot.removeAttribute("hidden");
      return;
    }
    this.chromeRoot.setAttribute("hidden", "");
  }

  isHUDOpen(): boolean {
    return this.isOpen;
  }

  isLayoutSettingsPanelOpen(): boolean {
    return this.isLayoutSettingsOpen;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.hide();
    this.chromeRoot.style.display = enabled ? "" : "none";
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private createChromeRoot(): HTMLDivElement {
    const root = document.createElement("div");
    root.setAttribute("data-string-grid-chrome", "");
    root.setAttribute("data-string-devtools-theme", "");
    root.setAttribute("hidden", "");
    root.setAttribute("string-isolation", "");
    root.setAttribute("data-string-isolation", "");
    return root;
  }

  private createTrigger(): HTMLButtonElement {
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.setAttribute("data-string-grid-trigger", "");
    trigger.setAttribute("data-open", "false");
    trigger.setAttribute("data-state", "visible");
    trigger.setAttribute("aria-label", "Open layout guides");
    trigger.setAttribute("aria-expanded", "false");
    trigger.innerHTML = this.icon("settings");
    trigger.addEventListener("click", (event) => {
      event.stopPropagation();
      this.toggle();
    });
    return trigger;
  }

  private createMainPanel(): HTMLDivElement {
    const panel = document.createElement("div");
    panel.setAttribute("data-string-grid-hud", "");
    panel.setAttribute("data-string-devtools-panel", "");
    panel.setAttribute("string-isolation", "");
    panel.setAttribute("data-string-isolation", "");

    const header = document.createElement("div");
    header.setAttribute("data-string-grid-panel-header", "");
    header.setAttribute("data-string-devtools-panel-header", "");

    this.layoutTitle = document.createElement("span");
    this.layoutTitle.setAttribute("data-string-grid-layout-summary", "");
    this.layoutTitle.setAttribute("data-string-devtools-panel-title", "");
    this.layoutTitle.textContent = "Layout";

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("data-string-grid-header-close", "");
    closeButton.setAttribute("data-string-devtools-icon-button", "");
    closeButton.setAttribute("aria-label", "Close layout guides");
    closeButton.innerHTML = this.icon("close");
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.hide();
    });

    header.appendChild(this.layoutTitle);
    header.appendChild(closeButton);

    this.layoutChips = document.createElement("div");
    this.layoutChips.setAttribute("data-string-grid-layout-chip-row", "");

    this.layoutSettingsToggle = document.createElement("button");
    this.layoutSettingsToggle.type = "button";
    this.layoutSettingsToggle.setAttribute("data-string-grid-layout-chip", "");
    this.layoutSettingsToggle.setAttribute("data-string-devtools-chip", "");
    this.layoutSettingsToggle.setAttribute("data-string-grid-layout-chip-action", "settings");
    this.layoutSettingsToggle.setAttribute("aria-label", "Toggle responsive layout settings");
    this.layoutSettingsToggle.innerHTML = this.icon("more");
    this.layoutSettingsToggle.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.isLayoutSettingsOpen) {
        this.closeLayoutPanel();
      } else {
        this.openLayoutPanel();
      }
    });

    this.listContainer = document.createElement("div");
    this.listContainer.setAttribute("data-string-grid-list", "");

    this.addGuideButton = document.createElement("button");
    this.addGuideButton.type = "button";
    this.addGuideButton.setAttribute("data-string-grid-empty-add", "");
    this.addGuideButton.setAttribute("data-string-devtools-button", "");
    this.addGuideButton.setAttribute("data-full-width", "true");
    this.addGuideButton.innerHTML = `<span data-string-grid-empty-add-icon>${this.icon("plus")}</span><span data-string-grid-empty-add-label>Add Layout Guide</span>`;
    this.addGuideButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.openAddPanel();
    });

    panel.appendChild(header);
    panel.appendChild(this.layoutChips);
    panel.appendChild(this.listContainer);
    panel.appendChild(this.addGuideButton);
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());
    return panel;
  }

  private createAddPanel(): HTMLDivElement {
    const panel = document.createElement("div");
    panel.setAttribute("data-string-grid-side-panel", "");
    panel.setAttribute("data-string-devtools-panel", "");
    panel.setAttribute("data-panel", "add");
    panel.setAttribute("data-open", "false");
    panel.setAttribute("string-isolation", "");
    panel.setAttribute("data-string-isolation", "");

    const header = document.createElement("div");
    header.setAttribute("data-string-grid-panel-header", "");
    header.setAttribute("data-string-devtools-panel-header", "");
    const title = document.createElement("span");
    title.setAttribute("data-string-grid-header-title", "");
    title.setAttribute("data-string-devtools-panel-title", "");
    title.textContent = "Add Layout Guide";
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("data-string-grid-header-close", "");
    closeButton.setAttribute("data-string-devtools-icon-button", "");
    closeButton.innerHTML = this.icon("close");
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeAddPanel();
    });
    header.appendChild(title);
    header.appendChild(closeButton);

    this.addGridList = document.createElement("div");
    this.addGridList.setAttribute("data-string-grid-add-grid-list", "");
    for (const [, adapter] of this.adapterRegistry) {
      const item = document.createElement("button");
      item.type = "button";
      item.setAttribute("data-string-grid-add-grid-item", "");
      item.setAttribute("data-string-devtools-button", "");
      item.setAttribute("data-full-width", "true");
      item.innerHTML = `<span data-string-grid-add-grid-icon>${adapter.icon}</span><span data-string-grid-add-grid-label>${adapter.label}</span>`;
      item.addEventListener("click", (event) => {
        event.stopPropagation();
        this.onAdd(adapter.type);
        this.closeAddPanel();
      });
      this.addGridList.appendChild(item);
    }

    panel.appendChild(header);
    panel.appendChild(this.addGridList);
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());
    return panel;
  }

  private createLayoutPanel(): HTMLDivElement {
    const panel = document.createElement("div");
    panel.setAttribute("data-string-grid-side-panel", "");
    panel.setAttribute("data-string-devtools-panel", "");
    panel.setAttribute("data-panel", "layout");
    panel.setAttribute("data-open", "false");
    panel.setAttribute("string-isolation", "");
    panel.setAttribute("data-string-isolation", "");

    const header = document.createElement("div");
    header.setAttribute("data-string-grid-panel-header", "");
    header.setAttribute("data-string-devtools-panel-header", "");
    const title = document.createElement("span");
    title.setAttribute("data-string-grid-header-title", "");
    title.setAttribute("data-string-devtools-panel-title", "");
    title.textContent = "Layout Settings";
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("data-string-grid-header-close", "");
    closeButton.setAttribute("data-string-devtools-icon-button", "");
    closeButton.setAttribute("aria-label", "Close layout settings");
    closeButton.innerHTML = this.icon("close");
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeLayoutPanel();
    });
    header.appendChild(title);
    header.appendChild(closeButton);

    this.layoutSettings = document.createElement("div");
    this.layoutSettings.setAttribute("data-string-grid-layout-settings", "");
    this.layoutSettingsList = document.createElement("div");
    this.layoutSettingsList.setAttribute("data-string-grid-layout-settings-list", "");

    this.layoutIOActions = document.createElement("div");
    this.layoutIOActions.setAttribute("data-string-grid-layout-io-row", "");

    const exportButton = document.createElement("button");
    exportButton.type = "button";
    exportButton.setAttribute("data-string-grid-io-btn", "");
    exportButton.setAttribute("data-string-devtools-button", "");
    exportButton.setAttribute("data-io", "export");
    exportButton.setAttribute("aria-label", "Export layouts");
    exportButton.innerHTML = `<span data-string-grid-layout-add-icon>${this.icon("export")}</span><span>Export</span>`;
    exportButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.onExport();
    });

    const importButton = document.createElement("button");
    importButton.type = "button";
    importButton.setAttribute("data-string-grid-io-btn", "");
    importButton.setAttribute("data-string-devtools-button", "");
    importButton.setAttribute("data-io", "import");
    importButton.setAttribute("aria-label", "Import layouts");
    importButton.innerHTML = `<span data-string-grid-layout-add-icon>${this.icon("import")}</span><span>Import</span>`;
    importButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.onImport();
    });

    this.layoutIOActions.appendChild(exportButton);
    this.layoutIOActions.appendChild(importButton);
    this.layoutSettings.appendChild(this.layoutSettingsList);
    this.layoutSettings.appendChild(this.layoutIOActions);

    panel.appendChild(header);
    panel.appendChild(this.layoutSettings);
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());
    return panel;
  }

  private createDetailPanel(): HTMLDivElement {
    const panel = document.createElement("div");
    panel.setAttribute("data-string-grid-side-panel", "");
    panel.setAttribute("data-string-devtools-panel", "");
    panel.setAttribute("data-panel", "detail");
    panel.setAttribute("data-open", "false");
    panel.setAttribute("string-isolation", "");
    panel.setAttribute("data-string-isolation", "");

    const header = document.createElement("div");
    header.setAttribute("data-string-grid-panel-header", "");
    header.setAttribute("data-string-devtools-panel-header", "");
    this.detailIcon = document.createElement("span");
    this.detailIcon.setAttribute("data-string-grid-header-icon", "");
    this.detailIcon.setAttribute("data-string-devtools-panel-icon", "");
    this.detailTitle = document.createElement("span");
    this.detailTitle.setAttribute("data-string-grid-detail-title", "");
    this.detailTitle.setAttribute("data-string-devtools-panel-title", "");
    this.detailTitle.addEventListener("click", (event) => {
      event.stopPropagation();
      this.startDetailRename();
    });
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("data-string-grid-header-close", "");
    closeButton.setAttribute("data-string-devtools-icon-button", "");
    closeButton.innerHTML = this.icon("close");
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeDetailPanel();
    });
    header.appendChild(this.detailIcon);
    header.appendChild(this.detailTitle);
    header.appendChild(closeButton);

    this.detailContent = document.createElement("div");
    this.detailContent.setAttribute("data-string-grid-detail-content", "");
    panel.appendChild(header);
    panel.appendChild(this.detailContent);
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());
    return panel;
  }
  private renderLayoutChips(): void {
    this.layoutChips.innerHTML = "";
    const activateChipLayout = (layoutId: string) => {
      this.selectedLayoutId = layoutId;
      this.renderLayoutChips();
      this.renderLayoutSettings();
    };

    this.layouts.forEach((layout, index) => {
      const mode = getGridLayoutMode(index);
      const chip = document.createElement("button");
      chip.type = "button";
      chip.setAttribute("data-string-grid-layout-chip", "");
      chip.setAttribute("data-string-devtools-chip", "");
      chip.setAttribute("data-selected", String(layout.id === this.selectedLayoutId));
      chip.setAttribute("data-resolved", String(layout.id === this.resolvedLayoutId));
      chip.setAttribute("data-mode", mode);
      chip.setAttribute("aria-label", `Select ${mode} layout`);
      chip.textContent = getModeLabel(mode);
      chip.addEventListener("click", (event) => {
        event.stopPropagation();
        activateChipLayout(layout.id);
        this.onSelectLayout(layout.id);
      });
      this.layoutChips.appendChild(chip);
    });
    this.layoutSettingsToggle.setAttribute("data-open", String(this.isLayoutSettingsOpen));
    this.layoutChips.appendChild(this.layoutSettingsToggle);
  }

  private renderLayoutSettings(): void {
    this.layoutSettingsList.innerHTML = "";
    const [large, medium, small] = this.layouts;
    if (!large || !medium || !small) {
      return;
    }

    this.layoutSettingsList.appendChild(this.createCombinedBreakpointControl(small, medium, large));
  }

  private createCombinedBreakpointControl(
    small: GridLayout,
    medium: GridLayout,
    large: GridLayout,
  ): HTMLDivElement {
    const root = document.createElement("div");
    root.setAttribute("data-string-grid-layout-range", "");
    root.setAttribute("data-state", "default");
    root.setAttribute("data-active-handle", "none");

    const scaleMin = 320;
    const scaleMax = 1920;
    const handleGap = 24;

    const labels = document.createElement("div");
    labels.setAttribute("data-string-grid-layout-range-labels", "");

    const firstLabel = document.createElement("button");
    firstLabel.type = "button";
    firstLabel.setAttribute("data-string-grid-layout-range-value", "");
    firstLabel.setAttribute("data-handle", "m");
    firstLabel.textContent = `${medium.minWidth}`;

    const secondLabel = document.createElement("button");
    secondLabel.type = "button";
    secondLabel.setAttribute("data-string-grid-layout-range-value", "");
    secondLabel.setAttribute("data-handle", "l");
    secondLabel.textContent = `${large.minWidth}`;

    const track = document.createElement("div");
    track.setAttribute("data-string-grid-layout-range-track", "");

    const segmentSmall = document.createElement("button");
    segmentSmall.type = "button";
    segmentSmall.setAttribute("data-string-grid-layout-range-segment", "");
    segmentSmall.setAttribute("data-mode", "S");
    segmentSmall.textContent = getModeLabel("S");

    const segmentMedium = document.createElement("button");
    segmentMedium.type = "button";
    segmentMedium.setAttribute("data-string-grid-layout-range-segment", "");
    segmentMedium.setAttribute("data-mode", "M");
    segmentMedium.textContent = getModeLabel("M");

    const segmentLarge = document.createElement("button");
    segmentLarge.type = "button";
    segmentLarge.setAttribute("data-string-grid-layout-range-segment", "");
    segmentLarge.setAttribute("data-mode", "L");
    segmentLarge.textContent = getModeLabel("L");

    const dividerMedium = document.createElement("button");
    dividerMedium.type = "button";
    dividerMedium.setAttribute("data-string-grid-layout-range-divider", "");
    dividerMedium.setAttribute("data-handle", "m");
    dividerMedium.setAttribute("data-handle-state", "default");
    dividerMedium.setAttribute("aria-label", "Adjust S/M breakpoint");

    const dividerLarge = document.createElement("button");
    dividerLarge.type = "button";
    dividerLarge.setAttribute("data-string-grid-layout-range-divider", "");
    dividerLarge.setAttribute("data-handle", "l");
    dividerLarge.setAttribute("data-handle-state", "default");
    dividerLarge.setAttribute("aria-label", "Adjust M/L breakpoint");

    const knobMedium = document.createElement("span");
    knobMedium.setAttribute("data-string-grid-layout-range-knob", "");
    knobMedium.setAttribute("data-handle", "m");
    knobMedium.setAttribute("data-handle-state", "default");

    const knobLarge = document.createElement("span");
    knobLarge.setAttribute("data-string-grid-layout-range-knob", "");
    knobLarge.setAttribute("data-handle", "l");
    knobLarge.setAttribute("data-handle-state", "default");

    const editor = document.createElement("div");
    editor.setAttribute("data-string-grid-layout-range-editor", "");
    editor.setAttribute("data-state", "default");
    editor.setAttribute("data-active-handle", "none");

    const editorPrefix = document.createElement("span");
    editorPrefix.setAttribute("data-string-grid-layout-range-editor-prefix", "");
    editorPrefix.innerHTML = this.getRangeHandleIcon("m");

    const editorInput = document.createElement("input");
    editorInput.type = "number";
    editorInput.inputMode = "numeric";
    editorInput.step = "1";
    editorInput.setAttribute("data-string-grid-layout-range-editor-input", "");

    const editorSuffix = document.createElement("span");
    editorSuffix.setAttribute("data-string-grid-layout-range-editor-suffix", "");
    editorSuffix.textContent = "px";

    labels.appendChild(firstLabel);
    labels.appendChild(secondLabel);

    track.appendChild(segmentSmall);
    track.appendChild(segmentMedium);
    track.appendChild(segmentLarge);
    track.appendChild(dividerMedium);
    track.appendChild(dividerLarge);
    track.appendChild(knobMedium);
    track.appendChild(knobLarge);

    root.appendChild(labels);
    root.appendChild(track);
    editor.appendChild(editorPrefix);
    editor.appendChild(editorInput);
    editor.appendChild(editorSuffix);
    root.appendChild(editor);

    const positionToValue = (position: number, width: number = track.getBoundingClientRect().width): number => {
      const safeWidth = Math.max(width, 1);
      const ratio = Math.max(0, Math.min(1, position / safeWidth));
      return Math.round(scaleMin + ratio * (scaleMax - scaleMin));
    };

    const valueToPercent = (value: number): number => {
      const ratio = (value - scaleMin) / (scaleMax - scaleMin);
      return Math.max(0, Math.min(1, ratio)) * 100;
    };

    const resolveSortedBounds = (firstValue: number, secondValue: number) => {
      const low = Math.min(firstValue, secondValue);
      const high = Math.max(firstValue, secondValue);
      return { low, high };
    };

    const resolveActiveLayoutId = (mediumValue: number, largeValue: number): string => {
      const viewportWidth = window.innerWidth;
      if (viewportWidth >= largeValue) {
        return large.id;
      }
      if (viewportWidth >= mediumValue) {
        return medium.id;
      }
      return small.id;
    };

    const setSelectedStates = () => {
      segmentSmall.setAttribute("data-selected", String(small.id === this.selectedLayoutId));
      segmentSmall.setAttribute("data-resolved", String(small.id === this.resolvedLayoutId));
      segmentMedium.setAttribute("data-selected", String(medium.id === this.selectedLayoutId));
      segmentMedium.setAttribute("data-resolved", String(medium.id === this.resolvedLayoutId));
      segmentLarge.setAttribute("data-selected", String(large.id === this.selectedLayoutId));
      segmentLarge.setAttribute("data-resolved", String(large.id === this.resolvedLayoutId));
    };

    const updateVisuals = (
      firstValue: number,
      secondValue: number,
      mediumValue: number,
      largeValue: number,
    ) => {
      const firstPercent = valueToPercent(firstValue);
      const secondPercent = valueToPercent(secondValue);
      const mediumPercent = valueToPercent(mediumValue);
      const largePercent = valueToPercent(largeValue);
      root.style.setProperty("--string-grid-layout-range-first", `${firstPercent}%`);
      root.style.setProperty("--string-grid-layout-range-second", `${secondPercent}%`);
      root.style.setProperty("--string-grid-layout-range-medium", `${mediumPercent}%`);
      root.style.setProperty("--string-grid-layout-range-large", `${largePercent}%`);
      firstLabel.textContent = `${firstValue}`;
      secondLabel.textContent = `${secondValue}`;
      setSelectedStates();
    };

    const clampHandle = (value: number) => Math.max(scaleMin, Math.min(scaleMax, value));

    let committedFirst = medium.minWidth;
    let committedSecond = large.minWidth;
    let draftFirst = committedFirst;
    let draftSecond = committedSecond;
    let editingHandle: "m" | "l" | null = null;
    let draggingHandle: "m" | "l" | null = null;
    let activeHandle: "m" | "l" = "m";

    const setHandleState = (handle: "m" | "l", state: "default" | "active" | "dragging") => {
      [dividerMedium, knobMedium, dividerLarge, knobLarge].forEach((element) => {
        if (element.getAttribute("data-handle") !== handle) {
          return;
        }
        element.setAttribute("data-handle-state", state);
      });
    };

    const syncInteractionState = () => {
      const currentState = draggingHandle ? "dragging" : editingHandle ? "active" : "default";
      const currentHandle = draggingHandle ?? editingHandle ?? activeHandle;
      root.setAttribute("data-state", currentState);
      root.setAttribute("data-active-handle", currentHandle);
      track.setAttribute("data-state", currentState);
      track.setAttribute("data-active-handle", currentHandle);
      editor.setAttribute("data-state", currentState);
      editor.setAttribute("data-active-handle", currentHandle);
      editor.setAttribute("data-handle", currentHandle);
      editorPrefix.innerHTML = this.getRangeHandleIcon(currentHandle);
      editorInput.value = String(currentHandle === "m" ? draftFirst : draftSecond);

      setHandleState("m", currentHandle === "m" ? currentState : "default");
      setHandleState("l", currentHandle === "l" ? currentState : "default");
    };

    const syncChipRowStates = () => {
      const chips = this.layoutChips.querySelectorAll("[data-string-grid-layout-chip][data-mode]");
      chips.forEach((chip, index) => {
        const layout = this.layouts[index];
        if (!layout) {
          return;
        }
        chip.setAttribute("data-selected", String(layout.id === this.selectedLayoutId));
        chip.setAttribute("data-resolved", String(layout.id === this.resolvedLayoutId));
      });
    };

    const activateSelectedLayout = (layoutId: string) => {
      this.selectedLayoutId = layoutId;
      syncChipRowStates();
      setSelectedStates();
    };

    const applyDraftState = (
      nextFirst: number,
      nextSecond: number,
      options?: {
        syncRuntime?: boolean;
        syncEditor?: boolean;
        syncSelectionToActive?: boolean;
      },
    ) => {
      draftFirst = clampHandle(nextFirst);
      draftSecond = clampHandle(nextSecond);
      const bounds = resolveSortedBounds(draftFirst, draftSecond);
      medium.minWidth = bounds.low;
      large.minWidth = bounds.high;
      const activeLayoutId = resolveActiveLayoutId(bounds.low, bounds.high);
      if (options?.syncSelectionToActive !== false) {
        this.selectedLayoutId = activeLayoutId;
      }
      this.resolvedLayoutId = activeLayoutId;
      const activeLayout = this.layouts.find((layout) => layout.id === activeLayoutId);
      this.layoutTitle.textContent = activeLayout ? getLayoutSummaryLabel(activeLayout) : "Layout";
      updateVisuals(draftFirst, draftSecond, bounds.low, bounds.high);
      syncChipRowStates();
      if (options?.syncEditor !== false && editingHandle) {
        editorInput.value = String(editingHandle === "m" ? draftFirst : draftSecond);
      }
      if (options?.syncRuntime !== false) {
        this.onPreviewResponsiveLayout(bounds.low, bounds.high, activeLayoutId);
      }
    };

    const closeEditor = () => {
      editingHandle = null;
      syncInteractionState();
    };

    const openEditor = (handle: "m" | "l") => {
      activeHandle = handle;
      editingHandle = handle;
      editor.setAttribute("data-handle", handle);
      editorInput.value = String(handle === "m" ? draftFirst : draftSecond);
      editorPrefix.innerHTML = this.getRangeHandleIcon(handle);
      syncInteractionState();
      editorInput.focus();
      editorInput.select();
    };

    const commitDraftState = () => {
      const bounds = resolveSortedBounds(draftFirst, draftSecond);
      const activeLayoutId = resolveActiveLayoutId(bounds.low, bounds.high);
      committedFirst = draftFirst;
      committedSecond = draftSecond;
      this.onCommitResponsiveLayout(bounds.low, bounds.high, activeLayoutId);
      closeEditor();
    };

    const startHandleInteraction = (
      handle: "m" | "l",
      anchor: HTMLElement,
      event: PointerEvent,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      activeHandle = handle;
      anchor.setPointerCapture(event.pointerId);
      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startRect = track.getBoundingClientRect();
      let hasDragged = false;
      draggingHandle = handle;
      syncInteractionState();

      const onMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) {
          return;
        }
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        const deltaX = moveEvent.clientX - startX;
        if (Math.abs(deltaX) > 2) {
          hasDragged = true;
        }
        const nextValue = positionToValue(moveEvent.clientX - startRect.left, startRect.width);
        if (handle === "m") {
          draftFirst = clampHandle(nextValue);
        } else {
          draftSecond = clampHandle(nextValue);
        }
        if (hasDragged && editingHandle !== handle) {
          openEditor(handle);
        }
        applyDraftState(draftFirst, draftSecond);
      };

      const onUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) {
          return;
        }
        upEvent.stopPropagation();
        anchor.releasePointerCapture(pointerId);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        draggingHandle = null;
        if (!hasDragged) {
          openEditor(handle);
          return;
        }
        syncInteractionState();
        commitDraftState();
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    };

    firstLabel.addEventListener("click", (event) => {
      event.stopPropagation();
      openEditor("m");
    });
    secondLabel.addEventListener("click", (event) => {
      event.stopPropagation();
      openEditor("l");
    });
    segmentSmall.addEventListener("click", (event) => {
      event.stopPropagation();
      activateSelectedLayout(small.id);
      this.onSelectLayout(small.id);
    });
    segmentMedium.addEventListener("click", (event) => {
      event.stopPropagation();
      activateSelectedLayout(medium.id);
      this.onSelectLayout(medium.id);
    });
    segmentLarge.addEventListener("click", (event) => {
      event.stopPropagation();
      activateSelectedLayout(large.id);
      this.onSelectLayout(large.id);
    });
    dividerMedium.addEventListener("pointerdown", (event) =>
      startHandleInteraction("m", dividerMedium, event),
    );
    dividerLarge.addEventListener("pointerdown", (event) =>
      startHandleInteraction("l", dividerLarge, event),
    );
    knobMedium.addEventListener("pointerdown", (event) =>
      startHandleInteraction("m", knobMedium, event),
    );
    knobLarge.addEventListener("pointerdown", (event) =>
      startHandleInteraction("l", knobLarge, event),
    );
    editorInput.addEventListener("input", (event) => {
      event.stopPropagation();
      const targetHandle = editingHandle ?? activeHandle;
      const parsed = Number.parseInt(editorInput.value, 10);
      if (!Number.isFinite(parsed)) {
        return;
      }
      if (targetHandle === "m") {
        applyDraftState(parsed, draftSecond);
        return;
      }
      applyDraftState(draftFirst, parsed);
    });
    editorInput.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        commitDraftState();
        return;
      }
      if (event.key === "Escape") {
        applyDraftState(committedFirst, committedSecond);
        closeEditor();
      }
    });
    editorInput.addEventListener("blur", () => {
      if (!editingHandle) {
        return;
      }
      commitDraftState();
    });

    applyDraftState(medium.minWidth, large.minWidth, {
      syncRuntime: false,
      syncSelectionToActive: false,
    });
    syncInteractionState();
    root.style.setProperty("--string-grid-layout-range-handle-gap", `${handleGap}px`);
    return root;
  }

  private renderList(): void {
    this.listContainer.innerHTML = "";
    for (const instance of this.instances) {
      const adapter = this.adapterRegistry.get(instance.type);
      if (!adapter) continue;
      const item = document.createElement("div");
      item.setAttribute("data-string-grid-list-item", "");
      item.setAttribute(
        "data-state",
        instance.id === this.selectedInstanceId ? "selected" : instance.visible ? "default" : "disabled",
      );
      item.innerHTML = `<span data-string-grid-list-icon>${adapter.icon}</span><span data-string-grid-list-label>${instance.name?.trim() || adapter.label}</span>`;
      const toggle = this.createToggle(instance.visible, () => this.onToggle(instance.id));
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.setAttribute("data-string-grid-list-delete", "");
      deleteButton.innerHTML = this.icon("delete");
      deleteButton.addEventListener("click", (event) => {
        event.stopPropagation();
        if (this.selectedInstanceId === instance.id) this.closeDetailPanel();
        this.onRemove(instance.id);
      });
      item.appendChild(toggle);
      item.appendChild(deleteButton);
      item.addEventListener("click", () => {
        if (this.selectedInstanceId === instance.id) this.closeDetailPanel();
        else this.selectInstance(instance.id);
      });
      this.listContainer.appendChild(item);
    }
    this.listContainer.style.display = this.instances.length === 0 ? "none" : "";
    this.addGuideButton.setAttribute("data-empty", String(this.instances.length === 0));
  }

  private renderDetailPanel(): void {
    if (!this.selectedInstanceId) {
      this.hideDetailPanelUI();
      return;
    }
    const instance = this.instances.find((entry) => entry.id === this.selectedInstanceId);
    if (!instance) {
      this.clearSelection();
      return;
    }
    const adapter = this.adapterRegistry.get(instance.type);
    if (!adapter) {
      this.clearSelection();
      return;
    }
    if (this.detailIcon) this.detailIcon.innerHTML = adapter.icon;
    if (this.detailTitle) this.detailTitle.textContent = instance.name?.trim() || adapter.label;
    this.detailBuilder?.destroy();
    this.detailBuilder = new GridUIBuilder();
    const settingsEl = this.detailBuilder.build(
      adapter.getUISchema(),
      instance.settings,
      (key, value) => this.onSettingChange(instance.id, key, value),
    );
    this.detailContent.innerHTML = "";
    this.detailContent.appendChild(settingsEl);
    this.detailPanel.setAttribute("data-open", "true");
    this.alignDetailPanel();
  }

  private createToggle(checked: boolean, onChange: () => void): HTMLLabelElement {
    const label = document.createElement("label");
    label.setAttribute("data-string-grid-list-toggle", "");
    label.setAttribute("data-checked", String(checked));
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.setAttribute("data-string-grid-toggle-input", "");
    input.addEventListener("change", (event) => {
      event.stopPropagation();
      onChange();
    });
    const knob = document.createElement("span");
    knob.setAttribute("data-string-grid-list-toggle-knob", "");
    label.addEventListener("click", (event) => event.stopPropagation());
    label.appendChild(input);
    label.appendChild(knob);
    return label;
  }

  private startDetailRename(): void {
    if (!this.selectedInstanceId || !this.detailTitle) return;
    const instance = this.instances.find((entry) => entry.id === this.selectedInstanceId);
    if (!instance) return;
    const adapter = this.adapterRegistry.get(instance.type);
    if (!adapter) return;
    const currentName = instance.name?.trim() || adapter.label;
    const parent = this.detailTitle.parentElement;
    if (!parent) return;
    const input = document.createElement("input");
    input.setAttribute("data-string-grid-detail-title-input", "");
    input.setAttribute("data-string-devtools-text-input", "");
    input.type = "text";
    input.value = currentName;
    const commit = () => {
      const nextName = input.value.trim();
      if (nextName !== currentName) this.onRenameInstance(instance.id, nextName);
      if (parent.contains(input)) parent.replaceChild(this.detailTitle!, input);
    };
    input.addEventListener("blur", commit);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") input.blur();
      if (event.key === "Escape") {
        input.value = currentName;
        input.blur();
      }
    });
    input.addEventListener("click", (event) => event.stopPropagation());
    input.addEventListener("pointerdown", (event) => event.stopPropagation());
    parent.replaceChild(input, this.detailTitle);
    input.focus();
    input.select();
  }

  private openAddPanel(): void {
    this.isAddPanelOpen = true;
    this.closeLayoutPanel();
    this.clearSelection();
    this.addPanel.setAttribute("data-open", "true");
  }

  private closeAddPanel(): void {
    this.isAddPanelOpen = false;
    this.addPanel.setAttribute("data-open", "false");
  }

  private openLayoutPanel(): void {
    this.isLayoutSettingsOpen = true;
    this.closeAddPanel();
    this.clearSelection();
    this.layoutPanel.setAttribute("data-open", "true");
    this.renderLayoutChips();
    this.alignLayoutPanel();
    this.onLayoutPanelOpen();
  }

  private closeLayoutPanel(): void {
    this.isLayoutSettingsOpen = false;
    this.layoutPanel.setAttribute("data-open", "false");
    this.renderLayoutChips();
    this.onLayoutPanelClose();
  }

  private alignLayoutPanel(): void {
    const btnRect = this.layoutSettingsToggle.getBoundingClientRect();
    const containerRect = this.panel.getBoundingClientRect();
    this.layoutPanel.style.top = `${btnRect.top - containerRect.top}px`;
  }

  private selectInstance(instanceId: string): void {
    this.selectedInstanceId = instanceId;
    this.closeLayoutPanel();
    this.closeAddPanel();
    this.renderList();
    this.renderDetailPanel();
  }

  private closeDetailPanel(): void {
    this.clearSelection();
    this.renderList();
  }

  private clearSelection(): void {
    this.selectedInstanceId = null;
    this.hideDetailPanelUI();
  }

  private hideDetailPanelUI(): void {
    const builder = this.detailBuilder;
    this.detailBuilder = null;
    this.detailPanel.setAttribute("data-open", "false");
    window.setTimeout(() => {
      if (this.detailPanel.getAttribute("data-open") === "false") {
        builder?.destroy();
        this.detailContent.innerHTML = "";
        this.detailPanel.style.top = "";
      }
    }, 220);
  }

  private alignDetailPanel(): void {
    const selectedItem = this.listContainer.querySelector(
      '[data-string-grid-list-item][data-state="selected"]',
    ) as HTMLElement | null;
    if (!selectedItem) return;
    const itemRect = selectedItem.getBoundingClientRect();
    const containerRect = this.panel.getBoundingClientRect();
    this.detailPanel.style.top = `${itemRect.top - containerRect.top}px`;
  }

  private show(): void {
    this.isOpen = true;
    this.trigger.setAttribute("data-open", "true");
    this.trigger.setAttribute("data-state", "open");
    this.trigger.setAttribute("aria-expanded", "true");
    this.onHUDOpen();
  }

  private hide(): void {
    this.isOpen = false;
    this.closeAddPanel();
    this.closeDetailPanel();
    this.closeLayoutPanel();
    this.trigger.setAttribute("data-open", "false");
    this.trigger.setAttribute("data-state", "visible");
    this.trigger.setAttribute("aria-expanded", "false");
    this.onHUDClose();
  }

  private toggle(): void {
    if (!this.enabled) return;
    if (this.isOpen) this.hide();
    else this.show();
  }

  private onDocumentPointerDown(event: PointerEvent): void {
    if (!this.isOpen) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (
      this.panel.contains(target) ||
      this.trigger.contains(target) ||
      this.addPanel.contains(target) ||
      this.layoutPanel.contains(target) ||
      this.detailPanel.contains(target)
    ) {
      return;
    }
    this.hide();
  }

  private deviceIcon(device: GridLayoutDevice): string {
    if (device === "desktop") {
      return `<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="3" width="11" height="8" rx="1.5" fill="currentColor"/><path d="M6 13h4" stroke="currentColor" stroke-linecap="round"/><path d="M4.5 14h7" stroke="currentColor" stroke-linecap="round"/></svg>`;
    }
    if (device === "tablet") {
      return `<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="4.25" y="2.25" width="7.5" height="11.5" rx="1.8" fill="currentColor"/><circle cx="8" cy="11.6" r="0.55" fill="#ffffff"/></svg>`;
    }
    return `<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="1.75" width="6" height="12.5" rx="2.4" fill="currentColor"/><circle cx="8" cy="11.8" r="0.55" fill="#ffffff"/></svg>`;
  }

  private getRangeHandleIcon(handle: "m" | "l"): string {
    return handle === "m" ? this.deviceIcon("tablet") : this.deviceIcon("desktop");
  }

  private icon(
    name:
      | "plus"
      | "close"
      | "delete"
      | "settings"
      | "more"
      | "chevron-up"
      | "chevron-down"
      | "export"
      | "import",
  ): string {
    switch (name) {
      case "plus":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 8H15" fill="none" stroke="currentColor"/><path d="M8 15V1" fill="none" stroke="currentColor"/></svg>`;
      case "close":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 2L14 14" fill="none" stroke="currentColor"/><path d="M2 14L14 2" fill="none" stroke="currentColor"/></svg>`;
      case "delete":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 8h8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`;
      case "settings":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M7.79199 1.17639C7.92907 1.14726 8.07093 1.14726 8.20801 1.17639C8.36286 1.20932 8.50859 1.29342 8.7998 1.46155L13.2617 4.03772C13.5531 4.20597 13.6997 4.29016 13.8057 4.40784C13.8994 4.51198 13.9704 4.63491 14.0137 4.76819C14.0626 4.91878 14.0625 5.08713 14.0625 5.42346V10.5758C14.0625 10.9123 14.0626 11.0814 14.0137 11.2321C13.9704 11.3651 13.8992 11.4874 13.8057 11.5914C13.6997 11.7091 13.5532 11.7933 13.2617 11.9615L8.7998 14.5377C8.50837 14.706 8.36293 14.7909 8.20801 14.8239C8.07093 14.853 7.92907 14.853 7.79199 14.8239C7.63707 14.7909 7.49163 14.706 7.2002 14.5377L2.73828 11.9615C2.44684 11.7933 2.30032 11.7091 2.19434 11.5914C2.10078 11.4874 2.02959 11.3651 1.98633 11.2321C1.93738 11.0814 1.9375 10.9123 1.9375 10.5758V5.42346C1.9375 5.08713 1.9374 4.91878 1.98633 4.76819C2.02963 4.63491 2.10057 4.51198 2.19434 4.40784C2.30031 4.29016 2.44687 4.20597 2.73828 4.03772L7.2002 1.46155C7.49141 1.29342 7.63714 1.20932 7.79199 1.17639ZM8 4.99964C6.34327 4.99964 5.0002 6.34295 5 7.99964C5 9.65649 6.34315 10.9996 8 10.9996C9.65685 10.9996 11 9.65649 11 7.99964C10.9998 6.34295 9.65673 4.99964 8 4.99964Z" fill="currentColor"/></svg>`;
      case "more":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="3" r="1.2" fill="currentColor"/><circle cx="8" cy="8" r="1.2" fill="currentColor"/><circle cx="8" cy="13" r="1.2" fill="currentColor"/></svg>`;
      case "chevron-up":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 10l5-5 5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      case "chevron-down":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      case "export":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v8" stroke="currentColor" stroke-linecap="round" fill="none"/><path d="M5 7l3 3 3-3" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" fill="none"/><path d="M2 13h12" stroke="currentColor" stroke-linecap="round" fill="none"/></svg>`;
      case "import":
        return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 10V2" stroke="currentColor" stroke-linecap="round" fill="none"/><path d="M5 5l3-3 3 3" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" fill="none"/><path d="M2 13h12" stroke="currentColor" stroke-linecap="round" fill="none"/></svg>`;
    }
  }
}
